import { Controller } from "@hotwired/stimulus"

// Batch selection controller for tables with checkboxes and action buttons
//
// Usage:
//   <div data-controller="batch-selection">
//     <table>
//       <thead>
//         <tr>
//           <th>
//             <input type="checkbox"
//                    data-action="change->batch-selection#toggleAll"
//                    data-batch-selection-target="selectAll">
//           </th>
//         </tr>
//       </thead>
//       <tbody>
//         <tr>
//           <td>
//             <input type="checkbox"
//                    name="deal_ids[]"
//                    value="1"
//                    data-action="change->batch-selection#toggle"
//                    data-batch-selection-target="checkbox">
//           </td>
//         </tr>
//       </tbody>
//     </table>
//
//     <!-- Sticky action bar, shown when items selected -->
//     <div data-batch-selection-target="actionBar" class="hidden">
//       <span data-batch-selection-target="count">0</span> selected
//
//       <!-- Action button that opens modal with selected items -->
//       <button data-batch-selection-target="action"
//               data-controller="modal"
//               data-action="click->modal#open"
//               data-modal-target-param="my_modal"
//               data-base-url="/deals/add_to_sale/new">
//         Add to Sale
//       </button>
//
//       <!-- Or form with hidden IDs -->
//       <form action="/batch_delete" method="post">
//         <div data-batch-selection-target="ids" data-name="deal_ids[]"></div>
//         <button type="submit">Delete</button>
//       </form>
//     </div>
//   </div>
//
// Targets:
// - checkbox: Individual row checkboxes (should have name="param_name[]" and value)
// - selectAll: Header checkbox to toggle all
// - count: Elements that display the selected count
// - actionBar: Container to show/hide based on selection
// - ids: Container where hidden inputs are auto-populated for form submission
// - action: Elements that receive data-params with selected values grouped by name
//           e.g., data-params='{"deal_ids[]":["1","2","3"]}'
//
export default class extends Controller {
  static targets = ["checkbox", "selectAll", "count", "actionBar", "ids", "action"]

  connect() {
    this.updateUI()
  }

  // Called when individual checkbox changes
  toggle() {
    this.updateUI()
  }

  // Called when select-all checkbox changes
  toggleAll(event) {
    const checked = event.target.checked
    this.visibleCheckboxes.forEach(cb => {
      cb.checked = checked
    })
    this.updateUI()
  }

  // Updates all UI elements based on current selection
  updateUI() {
    const count = this.selectedIds.length

    // Update count targets
    this.countTargets.forEach(el => {
      el.textContent = count
    })

    // Show/hide action bar
    if (this.hasActionBarTarget) {
      this.actionBarTarget.classList.toggle("hidden", count === 0)
    }

    // Update select-all checkbox state
    if (this.hasSelectAllTarget) {
      const visible = this.visibleCheckboxes
      const checkedCount = visible.filter(c => c.checked).length
      this.selectAllTarget.checked = visible.length > 0 && checkedCount === visible.length
      this.selectAllTarget.indeterminate = checkedCount > 0 && checkedCount < visible.length
    }

    // Update all ids containers with hidden inputs
    this.idsTargets.forEach(container => {
      this.populateIds(container)
    })

    // Update params on action targets (grouped by checkbox name)
    this.actionTargets.forEach(el => {
      el.dataset.params = JSON.stringify(this.selectedParams)
    })
  }

  // Populates a container with hidden inputs for selected IDs
  populateIds(container) {
    const name = container.dataset.name || "ids[]"
    container.innerHTML = this.selectedIds
      .map(id => `<input type="hidden" name="${name}" value="${id}">`)
      .join("")
  }

  // Returns array of selected checkbox values
  get selectedIds() {
    return this.checkboxTargets.filter(c => c.checked).map(c => c.value)
  }

  // Returns selected values grouped by checkbox name
  // e.g., { "deal_ids[]": ["1", "2", "3"] }
  get selectedParams() {
    const params = {}
    this.checkboxTargets
      .filter(c => c.checked && c.name)
      .forEach(c => {
        if (!params[c.name]) {
          params[c.name] = []
        }
        params[c.name].push(c.value)
      })
    return params
  }

  // Sync checkbox state from an external selection source (e.g. AG Grid).
  // Expects event.detail.selectedIds — an array of string IDs.
  //
  // Usage:
  //   data-action="grid:selectionChanged->batch-selection#syncSelection"
  syncSelection(event) {
    const selectedIds = new Set(event.detail?.selectedIds || [])
    this.checkboxTargets.forEach(cb => {
      cb.checked = selectedIds.has(cb.value)
    })
    this.updateUI()
  }

  // Returns checkboxes that are in visible, enabled rows
  get visibleCheckboxes() {
    return this.checkboxTargets.filter(c => {
      if (c.disabled) return false
      const row = c.closest("tr, [data-batch-selection-row]")
      return row && !row.classList.contains("hidden")
    })
  }

}
